/**
 * Faturas de um ou mais pagamentos — o drill-in da tela de Pagamentos.
 *
 * É esta lista que responde ao estado da linha: são as faturas com `paid_by` deste pagamento. Se
 * a soma dos valores recebidos fecha com o pagamento e todas estão pagas, ele está IDENTIFICADO;
 * fechando com faturas abertas, SINALIZADO; qualquer outra coisa, PENDENTE.
 *
 * Com vários pagamentos (AF-4139), cada linha diz a qual pagamento pertence pela "Ref. Extrato", a
 * conta do rodapé soma todos, e aprovar ou reprovar faz uma chamada por pagamento.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaCommentDots, FaInfoCircle, FaLayerGroup, FaTimes } from 'react-icons/fa';

import BadgeStatus from '../../../atoms/BadgeStatus';
import AddInvoiceCommentModal from './AddInvoiceCommentModal';
import InvoiceDetailsModal from './InvoiceDetailsModal';
import ApproveInvoicesDialog from './ApproveInvoicesDialog';
import BulkInvoiceActionsModal, { BulkAction } from './BulkInvoiceActionsModal';
import UnmarkInvoicesDialog from './UnmarkInvoicesDialog';
import CommentCell from '../../../molecules/CommentCell';
import PartyCell from '../../../molecules/PartyCell';
import { nominalAmountOf, openAmountOf, openAmountValue } from '../services/invoiceAmounts';
import {
    InvoicePaymentActionSettings, ParentAction, PaymentRow, PaymentSourceOption, addInvoicePaymentNote,
    addMarkingNote, getInvoicePaymentActionSettings, listInvoicesByPayments, listPaymentSources,
} from '../services/billingApi';
import { groupByPayment, paymentOfInvoice } from '../services/paymentsFilter';
import { CHARGE_SCENARIO_LABELS } from '../constants/chargeFilterSchema';
import { paymentSourceLabels } from '../../../../constants/payment';
import { ApprovalCell, approvalCell } from '../services/invoicePaymentAction';
import { PARENT_ACTION_ITEMS, PARENT_ACTION_LABELS } from '../constants/paymentActionPromise';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { STATUS_META } from '../PaymentsPanel';
import { modeOf } from '../constants/liquidationModes';
import TotalPartialBadge, { totalPartialBadge } from '../../../molecules/payment/TotalPartialBadge';
import PageFooter from '../../../molecules/PageFooter';

/* Coluna OCULTA, não removida: o `hidden` tira da VISTA e mantém no HTML, que é o que a tela de
   Faturas já faz com `#` e `Fatura`. O UUID encurtado não ajuda quem confere um pagamento, mas é
   por ele que se acha a linha no banco quando algo não bate. */
const OCULTA = 'hidden';

const ACTION_BUTTON = 'p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed';

const isSignalled = (inv: any): boolean => inv.paymentSignalled === true;
const isEffected = (inv: any): boolean => inv.status === 'PAID' && !isSignalled(inv);

/**
 * As anotações que esta lista mostra e grava: enquanto a fatura só está MARCADA, as da marcação
 * (vão para a fatura na aprovação); depois, as anotações de PAGAMENTO da fatura. Os comentários de
 * cobrança (`notes`) ficam na tela de Faturas.
 */
const paymentNotesOf = (inv: any): string[] =>
    (isSignalled(inv) && inv.marking ? inv.marking.notesList : inv.details?.paymentNotesList) || [];

const submitPaymentNote = (inv: any) => (text: string) =>
(isSignalled(inv) && inv.marking
    ? addMarkingNote(inv.marking.paymentSchedulingId, text)
    : addInvoicePaymentNote(inv.invoiceId, text));

/**
 * Acréscimo (+) ou desconto (−) da linha.
 *
 * Efetivada: o valor congelado na efetivação (`adjustmentAmount`, nulo quando a liquidação não foi
 * total). Sinalizada: a conta que a efetivação vai fazer, recebido menos o "Em Aberto" do título.
 */
export const adjustmentOf = (inv: any): number | null => {
    if (isSignalled(inv)) {
        if (modeOf(inv.totalPayment) !== 'TOTAL') return null;
        const open = openAmountValue(inv);
        return open == null ? null : Math.round((Number(inv.amountPaid || 0) - open) * 100) / 100;
    }
    if (isEffected(inv)) return inv.adjustmentAmount == null ? null : Number(inv.adjustmentAmount);
    return null;
};

/** Cor só em pagamento TOTAL: no parcial a diferença para o saldo não é desconto, é o que falta. */
export const adjustmentClass = (inv: any, adjustment: number | null): string => {
    if (modeOf(inv.totalPayment) !== 'TOTAL' || adjustment == null) return '';
    if (adjustment <= -0.005) return 'text-red-600';
    if (adjustment >= 0.005) return 'text-blue-600';
    return '';
};

export { totalPartialBadge };

const sumPaid = (list: any[]) => list.reduce((sum, inv) => sum + Number(inv.amountPaid || 0), 0);

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'gold' | 'olive' | 'teal';

const INVOICE_STATUS: Record<string, { label: string; color: BadgeColor }> = {
    OPEN: { label: 'Em aberto', color: 'gold' },
    PAID: { label: 'Paga', color: 'green' },
    CANCELLED: { label: 'Cancelada', color: 'red' },
};

/**
 * O que a linha DIZ quando a fatura está marcada neste pagamento.
 *
 * ⚠️ O status cru mente aqui. Esta é a lista de faturas de UM pagamento, e enquanto ele não for
 * efetivado todas elas estão apenas MARCADAS — dizer "Em aberto" esconde isso, e dizer "Cancelada"
 * é pior ainda: a tela de Identificação marca sempre a fatura ORIGINAL do título, que pode ter sido
 * cancelada numa reemissão, e o usuário fica sem entender onde a marcação dele foi parar.
 *
 * `yellow` é o `bg-yellow-500` — o amarelo de "tem dinheiro apontado, falta aprovar".
 */
const SIGNALLED = { label: 'Pagto Sinalizado', color: 'yellow' as BadgeColor };

interface Props {
    payments: PaymentRow[];
    onClose: () => void;
}

interface ModalProps extends Props {
    /** avisa a tela de trás para ela recarregar o dia — aprovar/reprovar aqui muda a linha de lá */
    onChanged?: () => void;
}

const PaymentInvoicesModal: React.FC<ModalProps> = ({ payments, onClose, onChanged }) => {
    const single = payments.length === 1 ? payments[0] : null;
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(false);
    const [commenting, setCommenting] = useState<any>(null);
    const [inspecting, setInspecting] = useState<any>(null);
    const [rejecting, setRejecting] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    /**
     * A cobrança anterior (AF-4062): a configuração que diz onde a aprovação decide, e o que o
     * aprovador escolheu em cada linha. Linha que ele não tocou mostra o marcado, senão o padrão.
     */
    const [actionSettings, setActionSettings] = useState<InvoicePaymentActionSettings | null>(null);
    const [choices, setChoices] = useState<Record<string, ParentAction | ''>>({});

    const [sources, setSources] = useState<PaymentSourceOption[]>([]);

    useEffect(() => {
        let alive = true;
        getInvoicePaymentActionSettings()
            .then(settings => { if (alive) setActionSettings(settings); })
            .catch(() => { /* sem a configuração, a coluna só mostra o que a marcação escolheu */ });
        listPaymentSources()
            .then(list => { if (alive) setSources(list || []); })
            .catch(() => { /* sem o catálogo, a Fonte mostra o rótulo fixo */ });
        return () => { alive = false; };
    }, []);

    const paymentOf = (inv: any): PaymentRow | undefined =>
        single || payments.find(p => p.paymentId === paymentOfInvoice(inv));

    const sourceName = (alias: string | null | undefined): string =>
        (alias && (sources.find(source => source.alias === alias)?.name || paymentSourceLabels[alias] || alias)) || '-';

    const cellOf = (inv: any): ApprovalCell => {
        const cell = approvalCell(inv, actionSettings);
        return cell.kind === 'SELECT' && inv.invoiceId in choices
            ? { kind: 'SELECT', value: choices[inv.invoiceId] } : cell;
    };

    const [loadError, setLoadError] = useState<string | null>(null);

    const reload = useCallback(async () => {
        try {
            setRows(await listInvoicesByPayments(payments.map(p => p.paymentId)));
            setLoadError(null);
        } catch (e: any) {
            setRows([]);
            setLoadError(e?.message || 'erro desconhecido');
        }
        setLoading(false);
    }, [payments]);

    useEffect(() => { reload(); }, [reload]);

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const currentPage = Math.min(page, totalPages - 1);
    const pageRows = rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    const selectable = useMemo(() => rows.filter(isSignalled), [rows]);
    const selectedRows = useMemo(
        () => selectable.filter(inv => selected.has(inv.invoiceId)), [selectable, selected]);
    const allSelected = selectable.length > 0 && selectedRows.length === selectable.length;
    const someSelected = selectedRows.length > 0 && !allSelected;

    const toggle = (invoiceId: string) => setSelected(current => {
        const next = new Set(current);
        if (next.has(invoiceId)) next.delete(invoiceId); else next.add(invoiceId);
        return next;
    });
    const toggleAll = (checked: boolean) =>
        setSelected(checked ? new Set(selectable.map(inv => inv.invoiceId)) : new Set());

    const afterAction = async () => {
        setSelected(new Set());
        setChoices({});
        await reload();
        onChanged?.();
    };

    const total = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const selectedGroups = groupByPayment(selectedRows, payments);
    const bulkAvailable = rows.some(inv => cellOf(inv).kind === 'SELECT');
    const signalledTotal = sumPaid(selectable);
    const effectedTotal = sumPaid(rows.filter(isEffected));
    const selectedTotal = sumPaid(selectedRows);
    const missing = total - signalledTotal - effectedTotal;
    const nothingSelected = selectedRows.length === 0;
    const NOTHING_SELECTED = 'Selecione ao menos uma fatura sinalizada';

    /** As escolhas das linhas selecionadas em que a aprovação decide — o que vai no corpo. */
    const selectedChoices = Object.fromEntries(selectedRows.flatMap(inv => {
        const cell = cellOf(inv);
        return cell.kind === 'SELECT' ? [[inv.invoiceId, cell.value]] : [];
    })) as Record<string, ParentAction | ''>;
    const missingChoice = Object.values(selectedChoices).some(value => !value);
    const MISSING_CHOICE = 'Escolha a cobrança anterior das faturas selecionadas';

    const applyBulk = (action: BulkAction) => {
        if (action.kind === 'PROMISE') {
            setChoices(prev => ({
                ...prev,
                ...Object.fromEntries(Object.keys(selectedChoices).map(id => [id, action.promise])),
            }));
        }
        setBulkOpen(false);
    };

    const previousInvoiceCell = (inv: any, shortId: string) => {
        const cell = cellOf(inv);
        if (cell.kind === 'SELECT') {
            return (
                <select aria-label={`Cobrança anterior da fatura ${shortId}`} value={cell.value}
                    onChange={e => setChoices(prev => ({
                        ...prev, [inv.invoiceId]: e.target.value as ParentAction | '',
                    }))}
                    className={`border rounded px-2 py-1 text-sm dark:bg-slate-700 dark:text-white ${cell.value
                        ? 'dark:border-slate-600' : 'border-amber-400'}`}>
                    <option value="">Selecione</option>
                    {PARENT_ACTION_ITEMS.map(item => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                </select>
            );
        }
        return (
            <span data-testid={`previous-${shortId}`}>
                {cell.kind === 'TEXT' ? PARENT_ACTION_LABELS[cell.choice] : '-'}
            </span>
        );
    };

    return (
        <>
            {/* ⚠️ O overlay fecha no clique, e por isso os modais FILHOS não moram dentro dele.
            Dentro, qualquer clique neles — inclusive focar o campo de texto — borbulhava até este
            `onClick` e fechava os dois de uma vez. Irmãos no mesmo fragmento, o clique do filho
            nunca passa por aqui. */}
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-[96rem] h-[85vh] flex flex-col"
                    onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                        <div>
                            <h3 className="text-lg font-semibold">
                                {single ? 'Faturas do pagamento' : `Faturas de ${payments.length} pagamentos`}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {single ? (
                                    <>
                                        #{single.paymentId.slice(0, 8)}
                                        {' · '}{STATUS_META[single.status].label}
                                        {' · '}valor {formatBRL(single.amount || 0)}
                                    </>
                                ) : <>valor somado {formatBRL(total)}</>}
                            </p>
                        </div>
                        <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>

                    <div className="flex-1 min-h-0 px-6 pb-4 overflow-y-auto">
                        {loading ? (
                            <p className="text-center text-gray-500 py-6">Carregando...</p>
                        ) : loadError ? (
                            <p role="alert" className="text-center text-red-600 py-6">
                                Não foi possível carregar as faturas: {loadError}
                            </p>
                        ) : rows.length === 0 ? (
                            <p className="text-center text-gray-500 py-6">
                                Nenhuma fatura sinalizada neste pagamento ainda.
                            </p>
                        ) : (
                            <>
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 dark:bg-slate-700 sticky top-0 z-10">
                                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                            {/* OCULTA, não removida: o UUID encurtado não diz nada
                                            a quem confere um pagamento e roubava a largura das
                                            colunas de parte, mas quem depura ainda o acha no HTML
                                            para localizar a linha no banco. Mesmo `hidden` das
                                            colunas `#` e `Fatura` da tela de Faturas. */}
                                            <th className="px-2 py-2 w-8">
                                                <input type="checkbox" className="rounded"
                                                    aria-label="Selecionar todas as faturas sinalizadas"
                                                    checked={allSelected}
                                                    disabled={selectable.length === 0}
                                                    ref={el => { if (el) el.indeterminate = someSelected; }}
                                                    onChange={e => toggleAll(e.target.checked)} />
                                            </th>
                                            <th className={`${OCULTA} px-2 py-2`}>Fatura</th>
                                            <th className="px-2 py-2 text-right whitespace-nowrap"
                                                title="O valor cheio do pagamento a que a fatura está ligada">Ref. Extrato</th>
                                            <th className="px-2 py-2">Contrato</th>
                                            {/* a OPERAÇÃO ao lado do CONTRATO, como nas telas */}
                                            <th className="px-2 py-2 whitespace-nowrap">Operação</th>
                                            {/* Quem ORIGINOU a dívida e quem a DEVE, nesta ordem e
                                            logo depois do contrato: é o bloco que identifica a
                                            dívida, antes dos números. Mesma ordem das telas de
                                            Títulos e de Faturas. */}
                                            {/* sem quebra: a coluna é estreita e o título de três
                                            palavras virava três linhas enquanto a lista carrega */}
                                            <th className="px-2 py-2 whitespace-nowrap max-w-[160px]">Emitente / Cedente</th>
                                            <th className="px-2 py-2 max-w-[160px]">Devedor</th>
                                            <th className="px-2 py-2">Natureza</th>
                                            <th className="px-2 py-2">Título/NF</th>
                                            <th className="px-2 py-2 text-center">Vencimento</th>
                                            {/* Três valores lado a lado, do que foi cobrado ao que
                                            falta: o nominal da fatura, o que este pagamento aponta
                                            para ela, e o saldo do TÍTULO. "Valor"/"Recebido" eram
                                            ambíguos — o primeiro não dizia de quê, e o segundo
                                            afirmava recebimento numa fatura que pode estar só
                                            sinalizada. */}
                                            <th className="px-2 py-2 text-right whitespace-nowrap">Valor Nominal</th>
                                            <th className="px-2 py-2 text-right whitespace-nowrap">Em Aberto</th>
                                            <th className="px-2 py-2 text-right whitespace-nowrap">Recebido</th>
                                            <th className="px-2 py-2 text-right whitespace-nowrap"
                                                title="Acréscimos / Descontos">Acrs./Desc.</th>
                                            <th className="px-2 py-2">Fonte</th>
                                            <th className="px-2 py-2 text-center" title="Total / Parcial">T/P</th>
                                            <th className="px-2 py-2 text-center">Status</th>
                                            {/* o que a efetivação faz com a fatura anterior (AF-4062) */}
                                            <th className="px-2 py-2 whitespace-nowrap" title="Ação sobre a Cobrança anterior">Cob. ant.</th>
                                            {/* entre Status e Ações, como na tela de Faturas */}
                                            <th className="px-2 py-2" title="Anotações de pagamento da fatura, ou da sinalização enquanto ela não é aprovada">Mensagem</th>
                                            <th className="px-2 py-2 text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map((inv: any) => {
                                            // marcada vence o status cru: PAGA nunca chega aqui
                                            // marcada (a efetivação apaga a marcação)
                                            const st = inv.paymentSignalled ? SIGNALLED
                                                : INVOICE_STATUS[inv.status]
                                                || { label: inv.status, color: 'gray' as BadgeColor };
                                            const shortId = String(inv.invoiceId).slice(0, 8);
                                            const adjustment = adjustmentOf(inv);
                                            return (
                                                <tr key={inv.invoiceId} className="border-t dark:border-slate-600">
                                                    <td className="px-2 py-2">
                                                        {isSignalled(inv) ? (
                                                            <input type="checkbox" className="rounded"
                                                                aria-label={`Selecionar fatura ${shortId}`}
                                                                checked={selected.has(inv.invoiceId)}
                                                                onChange={() => toggle(inv.invoiceId)} />
                                                        ) : (
                                                            <input type="checkbox" className="rounded" disabled
                                                                aria-label={`Fatura ${shortId} não selecionável`}
                                                                title={isEffected(inv) ? 'Já efetivada' : 'Não sinalizada neste pagamento'} />
                                                        )}
                                                    </td>
                                                    <td className={`${OCULTA} px-2 py-2 text-xs font-mono text-gray-500`} title={inv.invoiceId}>
                                                        {String(inv.invoiceId).slice(0, 8)}
                                                    </td>
                                                    <td className="px-2 py-2 text-right whitespace-nowrap">
                                                        {paymentOf(inv) ? formatBRL(paymentOf(inv)!.amount || 0) : '-'}
                                                    </td>
                                                    <td className="px-2 py-2 text-right">{inv.contractLabel || '-'}</td>
                                                    <td className="px-2 py-2 truncate max-w-[140px]">{inv.operationNumber || '-'}</td>
                                                    <td className="px-2 py-2 max-w-[160px]">
                                                        <PartyCell name={inv.issuer?.name}
                                                            documentNumber={inv.issuer?.documentNumber}
                                                            maxWidthClass="max-w-[160px]" />
                                                    </td>
                                                    <td className="px-2 py-2 max-w-[160px]">
                                                        <PartyCell name={inv.payer?.name}
                                                            documentNumber={inv.payer?.documentNumber}
                                                            maxWidthClass="max-w-[160px]" />
                                                    </td>
                                                    <td className="px-2 py-2">{CHARGE_SCENARIO_LABELS[inv.chargeScenario] || '-'}</td>
                                                    <td className="px-2 py-2">{inv.details?.documentTitle || '-'}</td>
                                                    <td className="px-2 py-2 text-center max-w-[110px]">{formatDateBR(inv.dueDate)}</td>
                                                    <td className="px-2 py-2 text-right">{nominalAmountOf(inv)}</td>
                                                    <td className="px-2 py-2 text-right">{openAmountOf(inv)}</td>
                                                    {/* A cor diz em que passo o dinheiro está, sem
                                                    obrigar a cruzar com a coluna de Status: âmbar é
                                                    promessa (sinalizado, esperando aprovação),
                                                    verde é fato (efetivado). É a MESMA convenção da
                                                    linha da tela de Pagamentos e das fatias da
                                                    barra do passo 3. */}
                                                    <td className={`px-2 py-2 text-right font-medium ${inv.paymentSignalled
                                                        ? 'text-yellow-600'
                                                        : inv.status === 'PAID' ? 'text-green-600' : ''}`}>
                                                        {formatBRL(inv.amountPaid || 0)}
                                                    </td>
                                                    <td className={`px-2 py-2 text-right whitespace-nowrap ${adjustmentClass(inv, adjustment)}`}>
                                                        {formatBRL(adjustment)}
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap">{sourceName(inv.paymentSource)}</td>
                                                    <td className="px-2 py-2 text-center">
                                                        <TotalPartialBadge totalPayment={inv.totalPayment}
                                                            postSettlement={inv.postSettlement === true} />
                                                    </td>
                                                    <td className="px-2 py-2 text-center whitespace-nowrap max-w-[140px]">
                                                        <BadgeStatus text={st.label} color={st.color} />
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap">
                                                        {previousInvoiceCell(inv, shortId)}
                                                    </td>
                                                    <td className="px-2 py-2 max-w-[220px] truncate">
                                                        <CommentCell notes={paymentNotesOf(inv)} />
                                                    </td>
                                                    <td className="px-2 py-2 text-center whitespace-nowrap">
                                                        {/* comentar vale em qualquer estado */}
                                                        <button
                                                            onClick={() => setCommenting(inv)}
                                                            aria-label={`Adicionar anotação de pagamento à fatura ${shortId}`}
                                                            title="Adicionar anotação de pagamento"
                                                            className={`${ACTION_BUTTON} text-blue-600 hover:text-blue-700`}>
                                                            <FaCommentDots />
                                                        </button>
                                                        <button
                                                            onClick={() => setInspecting(inv)}
                                                            aria-label={`Informações da fatura ${shortId}`}
                                                            title={isSignalled(inv) ? 'Informações da sinalização' : 'Informações da fatura'}
                                                            className={`${ACTION_BUTTON} text-slate-500 hover:text-slate-700 dark:text-slate-300`}>
                                                            <FaInfoCircle />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>

                    {!loading && rows.length > 0 && (
                        <div className="px-6 border-t border-gray-200 dark:border-slate-700">
                            <PageFooter
                                currentPage={currentPage}
                                totalPages={totalPages}
                                itemsPerPage={pageSize}
                                totalElements={rows.length}
                                onPageChange={setPage}
                                onItemsPerPageChange={size => { setPageSize(size); setPage(0); }}
                                itemsLabel="faturas"
                            />
                        </div>
                    )}

                    {/* a conta é o que decide o estado do pagamento — mostrá-la evita que o usuário
                    tenha de somar a coluna com o olho para entender por que a linha ainda está
                    pendente */}
                    {!loading && rows.length > 0 && (
                        <div className="flex items-center justify-between gap-4 px-6 py-3 border-t border-gray-300 dark:border-slate-500">
                            <p className="text-sm text-gray-700 dark:text-gray-200">
                                Sinalizado: <strong>{formatBRL(signalledTotal)}</strong>, Efetivado:{' '}
                                <strong>{formatBRL(effectedTotal)}</strong> de <strong>{formatBRL(total)}</strong>
                                {missing >= 0.005 && <> — faltam {formatBRL(missing)}</>}
                                {!nothingSelected && (
                                    <> — Selecionado <span className="text-blue-600">{formatBRL(selectedTotal)}</span></>
                                )}
                                .
                            </p>
                            <div className="flex gap-2 shrink-0">
                                {bulkAvailable && (
                                    <button onClick={() => setBulkOpen(true)} disabled={nothingSelected}
                                        title={nothingSelected ? NOTHING_SELECTED
                                            : 'Escolhe a cobrança anterior das faturas selecionadas'}
                                        className="px-4 py-2 text-sm rounded bg-blue-50 text-blue-700 dark:bg-slate-700 dark:text-blue-300 hover:bg-blue-100 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                                        <FaLayerGroup /> Ações em Lote
                                    </button>
                                )}
                                <button onClick={() => setApproving(true)} disabled={nothingSelected || missingChoice}
                                    title={nothingSelected ? NOTHING_SELECTED
                                        : missingChoice ? MISSING_CHOICE : 'Efetiva as faturas selecionadas'}
                                    className="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Aprovar
                                </button>
                                <button onClick={() => setRejecting(true)} disabled={nothingSelected}
                                    title={nothingSelected ? NOTHING_SELECTED : 'Remove a sinalização das faturas selecionadas'}
                                    className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Reprovar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {commenting && (
                <AddInvoiceCommentModal
                    invoice={commenting}
                    title="Adicionar anotação de pagamento"
                    submitNote={submitPaymentNote(commenting)}
                    onClose={() => setCommenting(null)}
                    onAdded={reload}
                />
            )}
            {inspecting && (
                <InvoiceDetailsModal invoice={inspecting} onClose={() => setInspecting(null)} />
            )}
            {approving && (
                <ApproveInvoicesDialog
                    groups={selectedGroups}
                    choices={selectedChoices}
                    onClose={() => setApproving(false)}
                    onApproved={afterAction}
                />
            )}
            {bulkOpen && (
                <BulkInvoiceActionsModal
                    selectedCount={selectedRows.length}
                    sources={[]}
                    promiseOnly
                    onApply={applyBulk}
                    onClose={() => setBulkOpen(false)}
                />
            )}
            {rejecting && (
                <UnmarkInvoicesDialog
                    groups={selectedGroups}
                    onClose={() => setRejecting(false)}
                    onUnmarked={afterAction}
                />
            )}
        </>
    );
};

export default PaymentInvoicesModal;
